import type { GridSpec } from '../grid.ts';
import { sizeClass, type FlowParams } from './params.ts';

/**
 * Dense-flow avalanche as Lagrangian particles with thickness-averaged physics, after the
 * approach of AvaFrame's com1DFA (method ported, not code):
 *
 * - Particles carry mass and a velocity tangent to the bed surface (the terrain plus the snow
 *   that stays behind). Gravity is projected on the local slope.
 * - Flow thickness is rebuilt every step by splatting moving particles' mass onto the terrain
 *   grid (bilinear); its gradient gives the lateral pressure that spreads the flow.
 * - Basal friction: Voellmy + minimum shear (params.ts), applied semi-implicitly so it can stop a
 *   particle but never reverse it.
 * - Entrainment: moving snow erodes the entrainable snowpack at rate τ_b·|u|/e_b; the picked-up
 *   mass joins the particle at rest (momentum conserved) and the bed is lowered. Heavy particles
 *   split to keep the resolution.
 * - Forest (optional): trees hold back snow at K/|u| per unit area and time (detrainment,
 *   Feistl et al. 2014); what they hold back is deposited in place. Slow flows lose the most.
 * - A particle that stays slower than stopSpeed for stopTime is deposited: its mass goes to the
 *   deposit field and raises the bed, so later snow flows over the debris.
 * - The run ends when nothing moves, when kinetic energy has fallen below 0.5 % of its peak, or
 *   at maxTime; anything still moving is deposited where it is.
 *
 * Coordinates: e = metres east of the grid's west edge, s = metres south of its north edge
 * (s grows with the row index), z = elevation. Mass is conserved exactly:
 * released + entrained = deposited + outflow.
 */

const G = 9.81;
/** Flow thickness floor (m) in the friction and entrainment terms. */
const H_MIN = 0.05;
const END_ENERGY_FRACTION = 0.005;
/** Speed floor (m/s) in the detrainment rate K/|u|, which diverges as the flow stops. */
const DETRAIN_MIN_SPEED = 1;
/** A particle whittled down by detrainment below this share of the particle mass is deposited. */
const DETRAIN_MIN_MASS = 0.05;
const MIN_RUN_TIME = 5;

export interface FlowInput {
  spec: GridSpec;
  /** Surface the flow runs on (m): terrain + snow left after the release is removed. */
  bed: Float32Array;
  /** Released snow per cell (kg/m²). */
  release: Float32Array;
  /** Density of the released snow per cell (kg/m³), for depth bookkeeping. */
  releaseDensity: Float32Array;
  /** Snow the flow may entrain per cell (kg/m²) and its density (kg/m³). */
  entrainable: Float32Array;
  entrainableDensity: Float32Array;
  /** Forest detrainment coefficient K (Pa) per cell; omitted or 0 = no forest. */
  detrainment?: Float32Array;
  params: FlowParams;
  /** Memory budget for recorded frames (bytes). */
  frameBudgetBytes?: number;
  /** Target time between recorded frames (s); doubled whenever the budget would overflow. */
  frameInterval?: number;
  onProgress?: (simTime: number, moving: number) => void;
}

/**
 * One recorded moment, quantised to keep memory small (9 bytes per particle). Decode with the
 * scales in FlowFrames. Particle indices are stable across frames; new particles (from splits)
 * are appended, so frame k+1 has at least as many as frame k.
 */
export interface FlowFrame {
  t: number;
  count: number;
  x: Uint16Array;
  y: Uint16Array;
  z: Uint16Array;
  /** Speed × SPEED_SCALE. */
  speed: Uint8Array;
  /** Flow thickness × THICKNESS_SCALE. */
  h: Uint8Array;
  /** bit0 stopped, bit1 gone (left the grid), bits 2–7 entrained fraction × 63. */
  flags: Uint8Array;
}

export const FRAME_SPEED_SCALE = 4; // 0.25 m/s steps, up to 63.75 m/s
export const FRAME_THICKNESS_SCALE = 20; // 5 cm steps, up to 12.75 m
export const FRAME_Z_STEP = 0.1; // m

export interface FlowFrames {
  frames: FlowFrame[];
  /** e = x / 65535 · width, s = y / 65535 · height, z = zMin + zq · FRAME_Z_STEP. */
  width: number;
  height: number;
  zMin: number;
  /** Mass of each particle at the end of the run (kg), for sizing sprites. */
  particleMass: Float32Array;
}

/** Centre of mass of the avalanche (moving and deposited snow) over time. */
export interface TrajectoryPoint {
  t: number;
  e: number;
  s: number;
  z: number;
  speed: number;
}

export interface RunoutStats {
  /** Top of the release (the highest release point). */
  top: { e: number; s: number; z: number };
  /** Runout tip: the farthest deposit along the path (99 % mass quantile). */
  tip: { e: number; s: number; z: number };
  /** Travel angle α (deg) from the top to the tip, horizontal distance along the path. */
  alphaDeg: number;
  /** β (deg): top to the point where the path first flattens below 10° (Lied & Bakkehøi). */
  betaDeg: number | null;
  /** α predicted from β: 0.96β − 1.4°, SD 2.3° (Lied & Bakkehøi 1980). */
  predictedAlphaDeg: number | null;
  /** Horizontal path length from top to tip (m). */
  reach: number;
  fallHeight: number;
}

export interface FlowStats {
  releasedKg: number;
  entrainedKg: number;
  /** Snow held back by forest (part of `depositedKg`). */
  detrainedKg: number;
  depositedKg: number;
  outflowKg: number;
  /** Mass-balance residual: released + entrained − deposited − outflow (kg). */
  balanceKg: number;
  releaseVolumeM3: number;
  entrainedVolumeM3: number;
  depositVolumeM3: number;
  depositAreaM2: number;
  maxDepositDepth: number;
  meanDepositDepth: number;
  maxSpeed: number;
  maxPressureKPa: number;
  durationS: number;
  particles: { initial: number; peak: number; massKg: number };
  sizeClass: 1 | 2 | 3 | 4 | 5;
  runout: RunoutStats | null;
  /**
   * More than 5 % of the moving snow left the loaded area: the runout (and α) only describe what
   * stayed inside, so the avalanche actually ran further. Load a larger area to see all of it.
   */
  leftArea: boolean;
}

export interface FlowResult {
  /** Per terrain cell (kg/m²): what left in the release, was eroded, and was deposited. */
  released: Float32Array;
  entrained: Float32Array;
  deposit: Float32Array;
  /** The part of `deposit` that came from entrained snow (kg/m²). */
  depositEntrained: Float32Array;
  /** Net snow-depth change (m): deposit − released − entrained, each at its own density. */
  netDepth: Float32Array;
  /** Peak values over the run. */
  maxSpeed: Float32Array;
  maxThickness: Float32Array;
  maxPressureKPa: Float32Array;
  trajectory: TrajectoryPoint[];
  frames: FlowFrames;
  stats: FlowStats;
}

/** Deterministic PRNG (mulberry32) so runs are reproducible. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function runFlow(input: FlowInput): FlowResult {
  const { spec, params: p } = input;
  const { cols, rows, cellSize: cs } = spec;
  const nCells = cols * rows;
  const cellArea = cs * cs;
  const width = cols * cs;
  const height = rows * cs;
  const bed = Float32Array.from(input.bed);
  const bedInitial = input.bed;
  const ent = Float32Array.from(input.entrainable);
  const random = rng(p.seed);

  // --- Sampling helpers (bilinear, cell-centred, clamped) ---------------------------------
  const sample = (f: Float32Array, e: number, s: number): number => {
    let fc = e / cs - 0.5;
    let fr = s / cs - 0.5;
    fc = fc < 0 ? 0 : fc > cols - 1 ? cols - 1 : fc;
    fr = fr < 0 ? 0 : fr > rows - 1 ? rows - 1 : fr;
    const c0 = fc | 0;
    const r0 = fr | 0;
    const c1 = c0 + 1 < cols ? c0 + 1 : c0;
    const r1 = r0 + 1 < rows ? r0 + 1 : r0;
    const tx = fc - c0;
    const ty = fr - r0;
    const a = f[r0 * cols + c0]!;
    const b = f[r0 * cols + c1]!;
    const c = f[r1 * cols + c0]!;
    const d = f[r1 * cols + c1]!;
    return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
  };
  /** Add `amount` at (e, s) spread bilinearly over the four surrounding cell centres. */
  const splat = (f: Float32Array, e: number, s: number, amount: number): void => {
    let fc = e / cs - 0.5;
    let fr = s / cs - 0.5;
    fc = fc < 0 ? 0 : fc > cols - 1 ? cols - 1 : fc;
    fr = fr < 0 ? 0 : fr > rows - 1 ? rows - 1 : fr;
    const c0 = fc | 0;
    const r0 = fr | 0;
    const c1 = c0 + 1 < cols ? c0 + 1 : c0;
    const r1 = r0 + 1 < rows ? r0 + 1 : r0;
    const tx = fc - c0;
    const ty = fr - r0;
    f[r0 * cols + c0] = f[r0 * cols + c0]! + amount * (1 - tx) * (1 - ty);
    f[r0 * cols + c1] = f[r0 * cols + c1]! + amount * tx * (1 - ty);
    f[r1 * cols + c0] = f[r1 * cols + c0]! + amount * (1 - tx) * ty;
    f[r1 * cols + c1] = f[r1 * cols + c1]! + amount * tx * ty;
  };
  const cellAt = (e: number, s: number): number => {
    const c = Math.min(cols - 1, Math.max(0, Math.floor(e / cs)));
    const r = Math.min(rows - 1, Math.max(0, Math.floor(s / cs)));
    return r * cols + c;
  };

  // --- Release → particles ------------------------------------------------------------------
  let releasedKg = 0;
  let releaseVolume = 0;
  const releaseCells: number[] = [];
  for (let i = 0; i < nCells; i++) {
    const m = input.release[i]!;
    if (!(m > 0)) continue;
    releaseCells.push(i);
    releasedKg += m * cellArea;
    releaseVolume += (m / input.releaseDensity[i]!) * cellArea;
  }
  const nInitial = releaseCells.reduce(
    (n, i) => n + Math.max(1, Math.round((input.release[i]! * cellArea) / p.particleMass)),
    0,
  );
  const capacity = Math.max(nInitial, Math.min(p.maxParticles, nInitial * 4 + 64));
  const pe = new Float32Array(capacity);
  const ps = new Float32Array(capacity);
  const pz = new Float32Array(capacity);
  const vx = new Float32Array(capacity);
  const vy = new Float32Array(capacity);
  const vz = new Float32Array(capacity);
  const pm = new Float32Array(capacity);
  const pmEnt = new Float32Array(capacity);
  const hAt = new Float32Array(capacity);
  /** Horizontal distance each particle has travelled (m). */
  const travelled = new Float32Array(capacity);
  const still = new Float32Array(capacity);
  const state = new Uint8Array(capacity); // 0 moving, 1 stopped, 2 gone
  let count = 0;
  for (const i of releaseCells) {
    const cellMass = input.release[i]! * cellArea;
    const k = Math.max(1, Math.round(cellMass / p.particleMass));
    const c = i % cols;
    const r = Math.floor(i / cols);
    for (let j = 0; j < k; j++) {
      pe[count] = (c + random()) * cs;
      ps[count] = (r + random()) * cs;
      pm[count] = cellMass / k;
      count++;
    }
  }

  // --- Fields --------------------------------------------------------------------------------
  const h = new Float32Array(nCells);
  const deposit = new Float32Array(nCells);
  const depositEntrained = new Float32Array(nCells);
  const entrained = new Float32Array(nCells);
  const maxSpeed = new Float32Array(nCells);
  const maxThickness = new Float32Array(nCells);
  const maxPressure = new Float32Array(nCells);
  let entrainedKg = 0;
  let entrainedVolume = 0;
  let detrainedKg = 0;
  /** Held back from the initial particles (the ones the runout tip is chosen from). */
  let heldBackInitialKg = 0;
  let depositedKg = 0;
  let outflowKg = 0;

  const deposit1 = (i: number): void => {
    const m = pm[i]!;
    const e = pe[i]!;
    const s = ps[i]!;
    splat(deposit, e, s, m / cellArea);
    splat(depositEntrained, e, s, pmEnt[i]! / cellArea);
    splat(bed, e, s, m / (p.debrisDensity * cellArea));
    pz[i] = sample(bed, e, s);
    depositedKg += m;
    state[i] = 1;
  };

  // --- Frames ----------------------------------------------------------------------------------
  const zMin = (() => {
    let z = Infinity;
    for (const v of bedInitial) if (v < z) z = v;
    return z;
  })();
  const frames: FlowFrame[] = [];
  const frameBudget = input.frameBudgetBytes ?? 96 * 1024 * 1024;
  let frameInterval = input.frameInterval ?? 0.2;
  let frameBytes = 0;
  let nextFrame = 0;
  const trajectory: TrajectoryPoint[] = [];
  const recordFrame = (t: number): void => {
    const f: FlowFrame = {
      t,
      count,
      x: new Uint16Array(count),
      y: new Uint16Array(count),
      z: new Uint16Array(count),
      speed: new Uint8Array(count),
      h: new Uint8Array(count),
      flags: new Uint8Array(count),
    };
    for (let i = 0; i < count; i++) {
      f.x[i] = Math.round((Math.min(width, Math.max(0, pe[i]!)) / width) * 65535);
      f.y[i] = Math.round((Math.min(height, Math.max(0, ps[i]!)) / height) * 65535);
      f.z[i] = Math.max(0, Math.min(65535, Math.round((pz[i]! - zMin) / FRAME_Z_STEP)));
      const sp = state[i] === 0 ? Math.hypot(vx[i]!, vy[i]!, vz[i]!) : 0;
      f.speed[i] = Math.min(255, Math.round(sp * FRAME_SPEED_SCALE));
      f.h[i] = Math.min(255, Math.round((state[i] === 0 ? hAt[i]! : 0) * FRAME_THICKNESS_SCALE));
      const entFrac = pm[i]! > 0 ? pmEnt[i]! / pm[i]! : 0;
      f.flags[i] = (state[i] === 1 ? 1 : state[i] === 2 ? 2 : 0) | (Math.round(entFrac * 63) << 2);
    }
    frames.push(f);
    frameBytes += count * 9;
    // Over budget: keep every other frame and halve the frame rate from here on.
    while (frameBytes > frameBudget && frames.length > 2) {
      const kept: FlowFrame[] = [];
      frameBytes = 0;
      for (let k = 0; k < frames.length; k += 2) {
        kept.push(frames[k]!);
        frameBytes += frames[k]!.count * 9;
      }
      frames.length = 0;
      frames.push(...kept);
      frameInterval *= 2;
    }
  };

  // --- Main loop -------------------------------------------------------------------------------
  let t = 0;
  let peakEnergy = 0;
  let peakCount = count;
  let peakSpeed = 0;
  // Bounding box (cells) of the last splat, to clear h cheaply.
  let bx0 = 0;
  let bx1 = -1;
  let by0 = 0;
  let by1 = -1;
  for (let i = 0; i < count; i++) pz[i] = sample(bed, pe[i]!, ps[i]!);

  for (;;) {
    // Stats over moving particles.
    let moving = 0;
    let fastest = 0;
    let energy = 0;
    let minE = Infinity;
    let maxE = -Infinity;
    let minS = Infinity;
    let maxS = -Infinity;
    let comE = 0;
    let comS = 0;
    let comZ = 0;
    let comM = 0;
    for (let i = 0; i < count; i++) {
      if (state[i] === 2) continue;
      // Centre of mass of all snow still in the grid (moving and deposited): unlike the moving
      // part alone, it doesn't jump back upslope when the front stops.
      comE += pm[i]! * pe[i]!;
      comS += pm[i]! * ps[i]!;
      comZ += pm[i]! * pz[i]!;
      comM += pm[i]!;
      if (state[i] !== 0) continue;
      moving++;
      const sp2 = vx[i]! ** 2 + vy[i]! ** 2 + vz[i]! ** 2;
      const sp = Math.sqrt(sp2);
      if (sp > fastest) fastest = sp;
      energy += 0.5 * pm[i]! * sp2;
      const e = pe[i]!;
      const s = ps[i]!;
      if (e < minE) minE = e;
      if (e > maxE) maxE = e;
      if (s < minS) minS = s;
      if (s > maxS) maxS = s;
    }
    if (energy > peakEnergy) peakEnergy = energy;
    if (fastest > peakSpeed) peakSpeed = fastest;
    if (t >= nextFrame || moving === 0) {
      recordFrame(t);
      nextFrame = t + frameInterval;
      if (comM > 0) {
        trajectory.push({ t, e: comE / comM, s: comS / comM, z: comZ / comM, speed: fastest });
      }
    }
    const ended =
      moving === 0 ||
      t >= p.maxTime ||
      (t > MIN_RUN_TIME && energy < END_ENERGY_FRACTION * peakEnergy);
    if (ended) {
      for (let i = 0; i < count; i++) if (state[i] === 0) deposit1(i);
      if (moving > 0) {
        // Replace this step's frame (if one was just recorded) with the final, all-stopped state.
        const last = frames[frames.length - 1];
        if (last && last.t === t) {
          frames.pop();
          frameBytes -= last.count * 9;
        }
        recordFrame(t);
      }
      break;
    }
    input.onProgress?.(t, moving);

    const dt = Math.min(0.1, Math.max(0.005, (0.4 * cs) / Math.max(fastest, 1)));

    // Flow thickness from moving particles.
    for (let r = by0; r <= by1; r++) h.fill(0, r * cols + bx0, r * cols + bx1 + 1);
    bx0 = Math.max(0, Math.floor(minE / cs) - 2);
    bx1 = Math.min(cols - 1, Math.floor(maxE / cs) + 2);
    by0 = Math.max(0, Math.floor(minS / cs) - 2);
    by1 = Math.min(rows - 1, Math.floor(maxS / cs) + 2);
    const toThickness = 1 / (p.flowDensity * cellArea);
    for (let i = 0; i < count; i++) {
      if (state[i] === 0) splat(h, pe[i]!, ps[i]!, pm[i]! * toThickness);
    }

    const countBefore = count;
    for (let i = 0; i < countBefore; i++) {
      if (state[i] !== 0) continue;
      const e = pe[i]!;
      const s = ps[i]!;
      // Bed normal from the local gradient (z up; e east; s south).
      const be = (sample(bed, e + cs, s) - sample(bed, e - cs, s)) / (2 * cs);
      const bs = (sample(bed, e, s + cs) - sample(bed, e, s - cs)) / (2 * cs);
      const inv = 1 / Math.sqrt(be * be + bs * bs + 1);
      const nx = -be * inv;
      const ny = -bs * inv;
      const nz = inv; // cos θ
      const hp = Math.max(H_MIN, sample(h, e, s));
      hAt[i] = hp;
      const he = (sample(h, e + cs, s) - sample(h, e - cs, s)) / (2 * cs);
      const hs = (sample(h, e, s + cs) - sample(h, e, s - cs)) / (2 * cs);

      // Gravity along the slope, plus lateral pressure −K·g·cosθ·∇h, both in the tangent plane.
      let ax = G * nz * nx;
      let ay = G * nz * ny;
      let az = -G + G * nz * nz;
      let px = -p.pressureCoef * G * nz * he;
      let py = -p.pressureCoef * G * nz * hs;
      let pzc = 0;
      const pd = px * nx + py * ny;
      px -= pd * nx;
      py -= pd * ny;
      pzc -= pd * nz;
      ax += px;
      ay += py;
      az += pzc;
      let ux = vx[i]! + dt * ax;
      let uy = vy[i]! + dt * ay;
      let uz = vz[i]! + dt * az;
      const vd = ux * nx + uy * ny + uz * nz;
      ux -= vd * nx;
      uy -= vd * ny;
      uz -= vd * nz;

      // Voellmy + τ₀ friction, semi-implicit: can stop the particle, never reverse it.
      let sp = Math.sqrt(ux * ux + uy * uy + uz * uz);
      const decel = p.mu * G * nz + (G * sp * sp) / (p.xi * hp) + p.tau0 / (p.flowDensity * hp);
      const slower = sp - dt * decel;
      if (slower <= 0) {
        ux = uy = uz = 0;
        sp = 0;
      } else {
        const f = slower / sp;
        ux *= f;
        uy *= f;
        uz *= f;
        sp = slower;
      }
      if (sp < p.stopSpeed) still[i] = still[i]! + dt;
      else still[i] = 0;
      vx[i] = ux;
      vy[i] = uy;
      vz[i] = uz;
      if (still[i]! >= p.stopTime) {
        deposit1(i);
        continue;
      }

      // Detrainment: forest holds back snow, deposited where it is (same velocity for the rest).
      if (input.detrainment) {
        const k = input.detrainment[cellAt(e, s)]!;
        if (k > 0) {
          const area = pm[i]! / (p.flowDensity * hp);
          const dm = Math.min(pm[i]!, (k / Math.max(sp, DETRAIN_MIN_SPEED)) * area * dt);
          if (pm[i]! - dm < DETRAIN_MIN_MASS * p.particleMass) {
            detrainedKg += pm[i]!;
            deposit1(i);
            continue;
          }
          const entShare = pmEnt[i]! / pm[i]!;
          splat(deposit, e, s, dm / cellArea);
          splat(depositEntrained, e, s, (dm * entShare) / cellArea);
          splat(bed, e, s, dm / (p.debrisDensity * cellArea));
          depositedKg += dm;
          detrainedKg += dm;
          if (i < nInitial) heldBackInitialKg += dm;
          pm[i] = pm[i]! - dm;
          pmEnt[i] = pmEnt[i]! - dm * entShare;
        }
      }

      // Entrainment of the snow under the flow.
      if (p.erosionEnergy > 0 && sp > 1) {
        const c = cellAt(e, s);
        const avail = ent[c]!;
        if (avail > 0) {
          const taub = p.flowDensity * hp * (p.mu * G * nz + (G * sp * sp) / (p.xi * hp));
          const area = pm[i]! / (p.flowDensity * hp);
          const dm = Math.min(((taub * sp) / p.erosionEnergy) * area * dt, avail * cellArea);
          if (dm > 0) {
            ent[c] = avail - dm / cellArea;
            entrained[c] = entrained[c]! + dm / cellArea;
            bed[c] = bed[c]! - dm / (input.entrainableDensity[c]! * cellArea);
            const f = pm[i]! / (pm[i]! + dm);
            vx[i] = vx[i]! * f;
            vy[i] = vy[i]! * f;
            vz[i] = vz[i]! * f;
            pm[i] = pm[i]! + dm;
            pmEnt[i] = pmEnt[i]! + dm;
            entrainedKg += dm;
            entrainedVolume += dm / input.entrainableDensity[c]!;
          }
        }
      }

      // Move (horizontal components; the particle then sits on the bed).
      const ne = e + vx[i]! * dt;
      const ns = s + vy[i]! * dt;
      if (ne < 0 || ne >= width || ns < 0 || ns >= height) {
        state[i] = 2;
        outflowKg += pm[i]!;
        continue;
      }
      travelled[i] = travelled[i]! + Math.hypot(ne - e, ns - s);
      pe[i] = ne;
      ps[i] = ns;
      pz[i] = sample(bed, ne, ns) + 0.5 * hp;

      const c = cellAt(ne, ns);
      if (sp > maxSpeed[c]!) maxSpeed[c] = sp;
      if (hp > maxThickness[c]!) maxThickness[c] = hp;
      const pressure = (p.flowDensity * sp * sp) / 1000;
      if (pressure > maxPressure[c]!) maxPressure[c] = pressure;

      // Split particles that entrainment has made heavy, while the budget allows.
      if (pm[i]! > 2 * p.particleMass && count < capacity) {
        const j = count++;
        const half = pm[i]! / 2;
        const halfEnt = pmEnt[i]! / 2;
        pm[i] = half;
        pmEnt[i] = halfEnt;
        pm[j] = half;
        travelled[j] = travelled[i]!;
        pmEnt[j] = halfEnt;
        // Offset sideways (perpendicular to the horizontal velocity).
        const hv = Math.hypot(vx[i]!, vy[i]!) || 1;
        const off = 0.15 * cs;
        pe[j] = Math.min(width - 1e-3, Math.max(0, ne - (vy[i]! / hv) * off));
        ps[j] = Math.min(height - 1e-3, Math.max(0, ns + (vx[i]! / hv) * off));
        pe[i] = Math.min(width - 1e-3, Math.max(0, ne + (vy[i]! / hv) * off));
        ps[i] = Math.min(height - 1e-3, Math.max(0, ns - (vx[i]! / hv) * off));
        pz[j] = pz[i]!;
        vx[j] = vx[i]!;
        vy[j] = vy[i]!;
        vz[j] = vz[i]!;
        hAt[j] = hp;
        still[j] = 0;
        state[j] = 0;
        if (count > peakCount) peakCount = count;
      }
    }
    t += dt;
  }

  // --- Results -----------------------------------------------------------------------------------
  const netDepth = new Float32Array(nCells);
  let depositArea = 0;
  let depositVolume = 0;
  let maxDepositDepth = 0;
  let maxPressureKPa = 0;
  for (let i = 0; i < nCells; i++) {
    const d = deposit[i]! / p.debrisDensity;
    const rel = input.release[i]! > 0 ? input.release[i]! / input.releaseDensity[i]! : 0;
    const er = entrained[i]! > 0 ? entrained[i]! / input.entrainableDensity[i]! : 0;
    netDepth[i] = d - rel - er;
    if (d > 0.05) depositArea += cellArea;
    depositVolume += d * cellArea;
    if (d > maxDepositDepth) maxDepositDepth = d;
    if (maxPressure[i]! > maxPressureKPa) maxPressureKPa = maxPressure[i]!;
  }

  const particleMass = Float32Array.from(pm.subarray(0, count));
  const runout = computeRunout({
    pe: pe.subarray(0, count),
    ps: ps.subarray(0, count),
    pz: pz.subarray(0, count),
    pm: pm.subarray(0, count),
    state: state.subarray(0, count),
    travelled: travelled.subarray(0, count),
    nInitial,
    heldBackKg: heldBackInitialKg,
    frames,
    width,
    height,
    cs,
    bedAt: (e, s) => sample(bedInitial, e, s),
    releaseDepthAt: (e, s) => {
      const c = cellAt(e, s);
      const m = input.release[c]!;
      return m > 0 ? m / input.releaseDensity[c]! : 0;
    },
  });

  return {
    released: Float32Array.from(input.release),
    entrained,
    deposit,
    depositEntrained,
    netDepth,
    maxSpeed,
    maxThickness,
    maxPressureKPa: maxPressure,
    trajectory,
    frames: { frames, width, height, zMin, particleMass },
    stats: {
      releasedKg,
      entrainedKg,
      detrainedKg,
      depositedKg,
      outflowKg,
      balanceKg: releasedKg + entrainedKg - depositedKg - outflowKg,
      releaseVolumeM3: releaseVolume,
      entrainedVolumeM3: entrainedVolume,
      depositVolumeM3: depositVolume,
      depositAreaM2: depositArea,
      maxDepositDepth,
      meanDepositDepth: depositArea > 0 ? depositVolume / depositArea : 0,
      maxSpeed: peakSpeed,
      maxPressureKPa,
      durationS: t,
      particles: { initial: nInitial, peak: peakCount, massKg: p.particleMass },
      sizeClass: sizeClass(depositVolume),
      runout,
      leftArea: outflowKg > 0.05 * (releasedKg + entrainedKg),
    },
  };
}

/**
 * α and β for the run, measured along the path of the snow that actually reached furthest:
 *
 * - Tip: among the particles present from the start, the one that travelled furthest (99 % mass
 *   quantile of travelled distance — snow held back by forest included, below all of them — so
 *   a few strays don't define it). Its path comes from the
 *   recorded frames.
 * - Top: straight upslope from where that particle started, to the crown of the release above it
 *   — Lied & Bakkehøi's "top of the starting zone" on that path. (Measuring from the release's
 *   single highest point, or along the centre of mass, goes wrong for wide releases that split
 *   into several gullies.)
 * - α: from the top to the tip; β: from the top to where that path first flattens below 10°
 *   (the path is extended by steepest descent if the avalanche stopped short of that point).
 */
export function computeRunout(o: {
  pe: Float32Array;
  ps: Float32Array;
  pz: Float32Array;
  pm: Float32Array;
  state: Uint8Array;
  travelled: Float32Array;
  nInitial: number;
  /**
   * Snow the forest held back on the way (kg): it stopped short of the particles that carried
   * it, so it counts in the mass quantile below every candidate.
   */
  heldBackKg?: number;
  frames: readonly FlowFrame[];
  width: number;
  height: number;
  cs: number;
  bedAt: (e: number, s: number) => number;
  releaseDepthAt: (e: number, s: number) => number;
}): RunoutStats | null {
  const { cs, bedAt, width, height } = o;
  const step = cs;

  // Tip particle: farthest travelled, 99 % mass quantile.
  const candidates: { d: number; m: number; i: number }[] = [];
  for (let i = 0; i < Math.min(o.nInitial, o.pe.length); i++) {
    if (o.state[i] === 1) candidates.push({ d: o.travelled[i]!, m: o.pm[i]!, i });
  }
  if (candidates.length === 0 || o.frames.length === 0) return null;
  candidates.sort((a, b) => b.d - a.d);
  let total = o.heldBackKg ?? 0;
  for (const c of candidates) total += c.m;
  let acc = 0;
  let tipIdx = candidates[0]!.i;
  for (const c of candidates) {
    acc += c.m;
    tipIdx = c.i;
    if (acc >= 0.01 * total) break;
  }

  // Its path through the frames.
  const flowPath: { e: number; s: number }[] = [];
  for (const f of o.frames) {
    if (tipIdx >= f.count) continue;
    const e = (f.x[tipIdx]! / 65535) * width;
    const s = (f.y[tipIdx]! / 65535) * height;
    const last = flowPath[flowPath.length - 1];
    if (!last || Math.hypot(e - last.e, s - last.s) > 0.25 * step) flowPath.push({ e, s });
  }
  flowPath.push({ e: o.pe[tipIdx]!, s: o.ps[tipIdx]! });
  const start = flowPath[0]!;

  // Crown above the start: step upslope while still inside the release.
  const up: { e: number; s: number }[] = [{ e: start.e, s: start.s }];
  for (let k = 0; k < 2000 / step; k++) {
    const c = up[up.length - 1]!;
    const ge = (bedAt(c.e + step, c.s) - bedAt(c.e - step, c.s)) / (2 * step);
    const gs = (bedAt(c.e, c.s + step) - bedAt(c.e, c.s - step)) / (2 * step);
    const g = Math.hypot(ge, gs);
    if (g < 1e-6) break;
    const next = { e: c.e + (ge / g) * step, s: c.s + (gs / g) * step };
    if (next.e < 0 || next.e > width || next.s < 0 || next.s > height) break;
    if (o.releaseDepthAt(next.e, next.s) <= 0) break;
    up.push(next);
  }
  const crown = up[up.length - 1]!;
  const top = {
    e: crown.e,
    s: crown.s,
    z: bedAt(crown.e, crown.s) + o.releaseDepthAt(crown.e, crown.s),
  };

  // Path: crown → start → the tip particle's own path → steepest-descent extension.
  const path = [...up.reverse(), ...flowPath.slice(1)];
  let cur = path[path.length - 1]!;
  for (let k = 0; k < 4000 / step; k++) {
    const ge = (bedAt(cur.e + step, cur.s) - bedAt(cur.e - step, cur.s)) / (2 * step);
    const gs = (bedAt(cur.e, cur.s + step) - bedAt(cur.e, cur.s - step)) / (2 * step);
    const g = Math.hypot(ge, gs);
    // Well past the 10° point (down to 3°) so the β search sees the transition.
    if (g < Math.tan((3 * Math.PI) / 180)) break;
    const next = { e: cur.e - (ge / g) * step, s: cur.s - (gs / g) * step };
    if (next.e < 0 || next.e > width || next.s < 0 || next.s > height) break;
    path.push(next);
    cur = next;
  }

  // Profile resampled every cell: horizontal distance along the path and bed elevation.
  const profile: { d: number; z: number }[] = [{ d: 0, z: top.z }];
  let dist = 0;
  let tipD = 0;
  const tipPathEnd = up.length + flowPath.length - 2; // index of the tip in `path`
  for (let k = 1; k < path.length; k++) {
    const a = path[k - 1]!;
    const b = path[k]!;
    const seg = Math.hypot(b.e - a.e, b.s - a.s);
    const n = Math.max(1, Math.ceil(seg / step));
    for (let j = 1; j <= n; j++) {
      const f = j / n;
      profile.push({ d: dist + seg * f, z: bedAt(a.e + (b.e - a.e) * f, a.s + (b.s - a.s) * f) });
    }
    dist += seg;
    if (k === tipPathEnd) tipD = dist;
  }

  // β: first point (past the first 50 m) where the slope along the path, averaged over 100 m,
  // drops below 10°. Lied & Bakkehøi read β off smoothed path profiles; on raw 5–10 m terrain a
  // short bench high up would otherwise count as "the slope flattens" and inflate β.
  let betaDeg: number | null = null;
  const window = Math.max(100, 5 * step);
  for (let k = 1; k < profile.length; k++) {
    const a = profile[k]!;
    if (a.d < 50) continue;
    let j = k;
    while (j < profile.length - 1 && profile[j]!.d - a.d < window) j++;
    const b = profile[j]!;
    if (b.d - a.d <= 0) break;
    if (Math.atan((a.z - b.z) / (b.d - a.d)) < (10 * Math.PI) / 180) {
      betaDeg = (Math.atan((top.z - a.z) / a.d) * 180) / Math.PI;
      break;
    }
  }

  const tipZ = o.pz[tipIdx]!;
  const reach = Math.max(tipD, 1);
  return {
    top,
    tip: { e: o.pe[tipIdx]!, s: o.ps[tipIdx]!, z: tipZ },
    alphaDeg: (Math.atan((top.z - tipZ) / reach) * 180) / Math.PI,
    betaDeg,
    predictedAlphaDeg: betaDeg === null ? null : 0.96 * betaDeg - 1.4,
    reach,
    fallHeight: top.z - tipZ,
  };
}
